// ClientsTestPage.jsx - Test page to verify Clients data from database

import React, { useEffect, useState } from 'react';
import { getClients } from '@/services/schedulingService';

const ClientsTestPage = () => {
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requestInfo, setRequestInfo] = useState({
    url: '',
    method: '',
    headers: {},
    response: null,
    errorDetails: null
  });

  useEffect(() => {
    async function fetchData() {
      try {
        console.log('=== ClientsTestPage: Starting fetch ===');
        const clientsData = await getClients();
        console.log('=== ClientsTestPage: Data received ===', clientsData);
        setRawData(clientsData);
        setRequestInfo(prev => ({
          ...prev,
          response: clientsData
        }));
      } catch (error) {
        console.error('=== ClientsTestPage: Error caught ===', error);
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
          response: error.response,
          request: error.request
        });
        setError(error.message || 'Failed to fetch clients data');
        setRequestInfo(prev => ({
          ...prev,
          errorDetails: {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data
          }
        }));
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem' }}>
        <h1>Clients Test Page</h1>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem' }}>
        <h1>Clients Test Page</h1>
        <div style={{ 
          background: '#ffebee', 
          padding: '1rem', 
          borderRadius: '4px',
          marginBottom: '1rem',
          border: '1px solid #f44336'
        }}>
          <h2 style={{ color: '#f44336', margin: '0 0 1rem 0' }}>Error</h2>
          <p style={{ color: '#c62828' }}>{error}</p>
        </div>

        {requestInfo.errorDetails && (
          <div style={{ marginBottom: '2rem' }}>
            <h2>Error Details</h2>
            <pre style={{ 
              background: '#f4f4f4', 
              padding: '1rem', 
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: '400px'
            }}>
              {JSON.stringify(requestInfo.errorDetails, null, 2)}
            </pre>
          </div>
        )}

        <div>
          <h2>Console Logs</h2>
          <p>Check browser console (F12) for detailed request/response logs</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', fontFamily: 'monospace' }}>
      <h1>Clients Test Page</h1>
      
      <div style={{ marginBottom: '2rem' }}>
        <h2>Data Summary</h2>
        <p><strong>Total Records:</strong> {rawData ? rawData.length : 0}</p>
        <p><strong>Data Type:</strong> {Array.isArray(rawData) ? 'Array' : typeof rawData}</p>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <h2>Raw Data (JSON)</h2>
        <pre style={{ 
          background: '#f4f4f4', 
          padding: '1rem', 
          borderRadius: '4px',
          overflow: 'auto',
          maxHeight: '400px'
        }}>
          {JSON.stringify(rawData, null, 2)}
        </pre>
      </div>

      <div>
        <h2>Table Format</h2>
        {rawData && rawData.length > 0 ? (
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            border: '1px solid #ddd'
          }}>
            <thead>
              <tr style={{ background: '#333', color: 'white' }}>
                <th style={{ padding: '0.75rem', border: '1px solid #ddd' }}>ClientID</th>
                <th style={{ padding: '0.75rem', border: '1px solid #ddd' }}>ClientCode</th>
                <th style={{ padding: '0.75rem', border: '1px solid #ddd' }}>Client</th>
                <th style={{ padding: '0.75rem', border: '1px solid #ddd' }}>Alias</th>
                <th style={{ padding: '0.75rem', border: '1px solid #ddd' }}>EngagementID</th>
                <th style={{ padding: '0.75rem', border: '1px solid #ddd' }}>EngagementName</th>
                <th style={{ padding: '0.75rem', border: '1px solid #ddd' }}>EngagementAlias</th>
              </tr>
            </thead>
            <tbody>
              {rawData.map((client, index) => (
                <tr key={index} style={{ background: index % 2 === 0 ? 'white' : '#f9f9f9' }}>
                  <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>{client.ClientID || '-'}</td>
                  <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>{client.ClientCode || '-'}</td>
                  <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>{client.Client || '-'}</td>
                  <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>{client.Alias || '-'}</td>
                  <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>{client.EngagementID || '-'}</td>
                  <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>{client.EngagementName || '-'}</td>
                  <td style={{ padding: '0.5rem', border: '1px solid #ddd' }}>{client.EngagementAlias || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>No data available</p>
        )}
      </div>

      <div style={{ marginTop: '2rem' }}>
        <h2>Field Analysis</h2>
        {rawData && rawData.length > 0 && (
          <div>
            <p><strong>Available Fields:</strong> {Object.keys(rawData[0]).join(', ')}</p>
            <p><strong>Sample Record:</strong></p>
            <pre style={{ 
              background: '#f4f4f4', 
              padding: '1rem', 
              borderRadius: '4px'
            }}>
              {JSON.stringify(rawData[0], null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientsTestPage;
